import Vue from 'vue';
import loopback from '@/lib/loopback';
import router from '@/router';

/**
 * Sync loopback token with current state
*/
export function syncToken({ commit, dispatch }) {
  if (loopback.token) {
    commit('setAccessToken', loopback.token);
    return dispatch(
      'loadAccount',
      loopback.token.userId,
    ).catch((err) => {
      commit('setAccessToken', null);
      return err;
    });
  }
  return Promise.resolve();
}

function evaluateRoute(state, to, from, next) {
  return (sessionError) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
      // this route requires auth, check if logged in
      // if not, redirect to login page (except when it's profile route and
      // there is an access_token).
      if (!state.access_token) {
        next({
          name: 'Login',
          params: {
            sessionError,
          },
        });
      } else if (to.meta.requiresAuth.length) {
        state.account.role.forEach((role) => {
          if (to.meta.requiresAuth.includes(role.name)) {
            next();
          } else {
            next({
              name: 'Proibido',
            });
          }
        });
      } else {
        next();
      }
    } else {
      next(); // make sure to always call next()!
    }
  };
}

/**
 * Sync router for auth
 */
export function syncRouter({ state, dispatch }, myRouter) {
  myRouter.beforeEach((to, from, next) => {
    dispatch('syncToken').then(
      evaluateRoute(state, to, from, next),
    );
  });
}

/**
 * Sign-in account with email and password (stores in state.account)
 * @param  {Function} commit   commit mutations function
 * @param  {String}   email    user email
 * @param  {String}   password user password
 * @return {Promise}           promise of logged user
 */
export function signIn({ commit, dispatch, state }, { email, password }) {
  return loopback
    .post('/Accounts/login', {
      email,
      password,
    })
    .then((token) => {
      commit('setAccessToken', token);

      // Update Loopback Token
      if (state.access_token !== null) {
        loopback.setToken(state.access_token);
      } else {
        loopback.removeToken();
      }

      return dispatch('loadAccount', state.access_token.userId);
    });
}

/**
 * Sign-out current logged-in account
 *  (assumes that current state.account is not null)
 * @param  {Function} commit commit mutations function
 * @return {Promise}         promise of the logout
 */
export function signOut({ commit, state }) {
  return loopback
    .post('/Accounts/logout', {
      // eslint-disable-next-line dot-notation
      accessToken: state['access_token'],
    })
    .then(() => {
      commit('setAccessToken', null);
      commit('setAccount', null);
      loopback.token = null;
      loopback.removeToken();
      router.push({ name: 'Login' });
    });
}

/**
 * Load an account by userId in state.account
 * @param  {Function} commit    commit mutations function
 * @param  {Number}   userId account id
 * @return {Promise}            promise of the account
 */
export function loadAccount({ commit }, userId) {
  return loopback
    .find(`/Accounts/${userId}`, { include: ['role'] })
    .then((acc) => {
      commit('setAccount', acc);
      return acc;
    })
    .catch((err) => {
      loopback.removeToken();
      return Promise.reject(err);
    });
}

/**
 * Reset the password of the current logged-in account
 *  (assumes that current state.account is not null)
 *  (assumes that current state.access_token is not null)
 * @param  {Function} commit       commit mutations function
 * @param  {Object}   state        Vuex state
 * @param  {String}   oldPassword  old password
 * @param  {String}   newPassword  new password
 * @return {Promise}              promise of the password reset
 */
export function changePassword(ctx, { oldPassword, newPassword }) {
  return loopback
    .post(
      '/Accounts/change-password',
      { oldPassword, newPassword },
    );
}

export function resetPassword(ctx, { newPassword, accessToken }) {
  return loopback
    .post(
      `/Accounts/reset-password?access_token=${accessToken}`,
      { newPassword },
    );
}

/**
 * Send a email to the account user to reset the password
 * @param  {Function} commit       commit mutations function
 * @param  {String}   email        user email
 * @return {Promise}               promise of the sent email
 */
export function rememberPassword(ctx, email) {
  return loopback
    .post('/Accounts/reset', { email });
}

export function signUp(ctx, payload) {
  return loopback
    .post('/Accounts', payload)
    .then((accessToken) => {
      Vue.toasted.success('Usuário criado com sucesso.');
      return accessToken;
    });
}
